import { styled, x } from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Redirect, useHistory } from 'react-router-dom';
import { t } from 'ttag';
import { LearnerTrainingStatus, usePostReviewMutation } from '~/data';
import { select } from '~/lib/select';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Logo } from '~/ui/logo/Logo';
import { Spinner } from '~/ui/spinner/Spinner';
import { TrainingState, useTrainingStore } from '../state';
import { useTrainingUrl } from '../useTrainingUrl';
import { AcceptPolicy } from './AcceptPolicy';
import { FeedbackForm } from './FeedbackForm';
import { postMessage } from '~/shell/PostMessage';
import { useTrainingParams } from '../useTrainingParams';
import { useLmsAdapter } from '~/lms';

const selector = (s: TrainingState) =>
  select(
    s,
    'status',
    'logoUrl',
    'scorePercent',
    'policyAcknowledgement',
    'featureShowReviewStep',
    'policiesForSignature',
    'certificateUrl'
  );

const ContainerForFeedback = styled.div`
  width: 550px;
  margin-left: auto;
  margin-right: auto;

  .LlamaModalOverlay {
    position: unset;
  }

  @media (max-width: sm) {
    width: 100%;

    .modalStyleForDarkTheme > svg:first-of-type {
      display: none;
    }
  }
`;

const StyledLlamaModal = styled(LlamaModal)`
  .instructions {
    font-size: 14px;
    line-height: 20px;
  }
  @media (max-width: sm) {
    padding-top: 0px !important;
    align-items: center !important;

    div:first-child {
      padding-top: 10px;

      .FullLlamaImg {
        display: none;
      }

      .PointerForModal {
        display: none;
      }
    }
  }
`;

export const Feedback = () => {
  const {
    status,
    logoUrl,
    scorePercent,
    policyAcknowledgement,
    featureShowReviewStep,
    policiesForSignature,
    certificateUrl
  } = useTrainingStore(selector);
  const [postReview, { called, loading, data, error }] =
    usePostReviewMutation();
  const url = useTrainingUrl();
  const history = useHistory();
  const { learnerTrainingId } = useTrainingParams();
  const lms = useLmsAdapter();
  const successPath = `${url}/success`;

  // This governs whether the policy acceptance component should be shown.
  // The default is true if there is a policy and the config says it needs to be acknowledged
  const [policyAccepted, setPolicyAccepted] = useState(
    !(
      policyAcknowledgement &&
      policiesForSignature &&
      policiesForSignature.length
    )
  );

  useEffect(() => {
    // Hooks cannot sit below the Completed Redirect — gate completion signals
    // here so we never postMessage / LMS pass while still redirecting away.
    if (status !== LearnerTrainingStatus.Completed || !policyAccepted) return;

    postMessage(
      learnerTrainingId || '',
      certificateUrl || '',
      'completed',
      scorePercent
    );

    if (lms.isAvailable) {
      lms.setScore(scorePercent, 100, 0);
      lms.setReachedEnd();
      lms.setPassed();
    }

    if (window && window.document) {
      window.document.body.style.backgroundColor = 'var(--color-green-lt)';
    }
  }, [status, policyAccepted]);

  /**
   * CAT-922: Send must leave Almost There even when the course still has
   * `policiesForSignature` in the store (already signed before this form).
   * Parent used to gate Redirect on an empty policies array and remount an
   * empty FeedbackForm after the mutation spinner.
   */
  const submitReview: typeof postReview = async options => {
    const result = await postReview(options);
    const payloadErrors = result.data?.review?.errors;
    const hasGraphQlErrors = Boolean(result.errors?.length);
    const hasPayloadErrors = (payloadErrors?.length ?? 0) > 0;

    if (!hasGraphQlErrors && !hasPayloadErrors) {
      history.push(successPath);
    }

    return result;
  };

  if (status !== LearnerTrainingStatus.Completed) {
    // We don't show this
    return <Redirect to={url!} />;
  }

  if (loading) {
    return (
      <LlamaModal vertical="center" horizontal="center">
        <Spinner />
      </LlamaModal>
    );
  }

  if (error || (data?.review?.errors?.length ?? 0) > 0) {
    return (
      <LlamaModal vertical="center" horizontal="center">
        <x.p p="lg">{t`We're sorry, an unexpected error has occurred`}</x.p>
      </LlamaModal>
    );
  }

  // Successful review (backup if history.push raced) — always leave, policies
  // were already acknowledged before FeedbackForm.
  if (called) {
    return <Redirect to={successPath} />;
  }

  // No review step and nothing left to sign — skip this page.
  if (
    !featureShowReviewStep &&
    (!policiesForSignature || policiesForSignature.length === 0)
  ) {
    return <Redirect to={successPath} />;
  }

  return createPortal(
    <ContainerForFeedback>
      <StyledLlamaModal>
        <Flex
          variant="list"
          p="lg"
          gap="10px"
          maxW="395px"
          textAlign="center"
          paddingLeft="20px"
          paddingRight="20px"
          margin="auto"
        >
          <x.div display="flex" justifyContent="center">
            <Logo src={logoUrl} height="36" />
          </x.div>
          <x.h2 fontFamily="llama" fontSize="36px">{t`Almost There!`}</x.h2>
          {featureShowReviewStep && policyAccepted ? (
            <FeedbackForm postReview={submitReview} />
          ) : (
            <AcceptPolicy
              accept={() => {
                if (
                  !featureShowReviewStep &&
                  policiesForSignature &&
                  policiesForSignature?.length
                ) {
                  window.location.href = successPath;
                } else {
                  setPolicyAccepted(true);
                }
              }}
            />
          )}
        </Flex>
      </StyledLlamaModal>
    </ContainerForFeedback>,
    document.getElementById('root')!
  );
};
